import React, { useState } from "react";
import { Compass, ArrowLeft, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const QUESTIONS = [
  { key: "sync", q: "Should DPP sync your brokerage automatically?", opts: [
    { v: "yes", label: "Yes, keep my real holdings up to date" },
    { v: "no", label: "No, I'll add holdings myself or just plan" },
  ] },
  { key: "portfolios", q: "How many portfolio scenarios will you keep?", opts: [
    { v: "1", label: "Just one" },
    { v: "few", label: "2 to 10" },
    { v: "many", label: "More than 10" },
  ] },
  { key: "ai", q: "How often will you use the AI tools (Income Planner, Optimizer, ticker thesis)?", opts: [
    { v: "low", label: "A few times a month" },
    { v: "mid", label: "About once a week" },
    { v: "high", label: "Whenever I want, no limits" },
  ] },
  { key: "optimize", q: "Do you want AI to rebalance the holdings you already own?", opts: [
    { v: "yes", label: "Yes, show me the exact trades" },
    { v: "no", label: "Not right now" },
  ] },
];

// Pure scoring: highest tier any answer needs wins.
export function recommendPlan(a) {
  const pro = [], core = [];
  if (a.optimize === "yes") pro.push("Holdings Optimizer with a whole-share trade list");
  if (a.portfolios === "many") pro.push("Unlimited saved portfolios");
  if (a.ai === "high") pro.push("Unlimited AI actions");
  if (a.sync === "yes") core.push("Read-only brokerage sync");
  if (a.portfolios === "few") core.push("Up to 10 saved portfolios");
  if (a.ai === "mid") core.push("10 AI actions a month");
  if (pro.length) return { plan: "pro", reasons: [...pro, ...core] };
  if (core.length) return { plan: "core", reasons: core };
  return { plan: "free", reasons: ["1 portfolio with the full 30-year backtest engine", "4 AI actions a month + Ask DPP", "Upgrade anytime when you need more"] };
}

const PLAN_LABEL = { free: "Free", core: "Core", pro: "Pro" };

export default function PlanPickerQuiz({ open, onOpenChange, onPick }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const done = step >= QUESTIONS.length;
  const rec = done ? recommendPlan(answers) : null;

  const reset = () => { setStep(0); setAnswers({}); };
  const choose = (k, v) => { setAnswers((a) => ({ ...a, [k]: v })); setStep((s) => s + 1); };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-w-md" data-testid="plan-quiz">
        <DialogHeader>
          <DialogTitle className="serif text-2xl flex items-center gap-2"><Compass className="w-5 h-5 text-secondary" /> Find your plan</DialogTitle>
          <DialogDescription>{done ? "Here's the plan that fits how you invest." : `Question ${step + 1} of ${QUESTIONS.length}`}</DialogDescription>
        </DialogHeader>
        {!done ? (
          <div>
            <div className="h-1.5 rounded-full bg-muted overflow-hidden">
              <div className="h-full bg-secondary transition-[width] duration-300" style={{ width: `${(step / QUESTIONS.length) * 100}%` }} />
            </div>
            <p className="font-semibold mt-4">{QUESTIONS[step].q}</p>
            <div className="mt-3 space-y-2">
              {QUESTIONS[step].opts.map((o) => (
                <button key={o.v} type="button" onClick={() => choose(QUESTIONS[step].key, o.v)}
                  className="w-full text-left rounded-md border border-border px-4 py-3 text-sm hover:border-secondary hover:bg-secondary/5 transition-colors"
                  data-testid={`plan-quiz-${QUESTIONS[step].key}-${o.v}`}>
                  {o.label}
                </button>
              ))}
            </div>
            {step > 0 && (
              <button type="button" onClick={() => setStep((s) => s - 1)} className="mt-3 text-xs text-muted-foreground inline-flex items-center gap-1 hover:text-primary" data-testid="plan-quiz-back">
                <ArrowLeft className="w-3 h-3" /> Back
              </button>
            )}
          </div>
        ) : (
          <div data-testid="plan-quiz-result">
            <div className="rounded-lg border-2 border-secondary/50 bg-secondary/5 p-4">
              <div className="mono text-[10px] uppercase tracking-widest text-secondary">We recommend</div>
              <div className="serif text-3xl mt-1" data-testid="plan-quiz-recommendation">{PLAN_LABEL[rec.plan]}</div>
              <ul className="mt-3 space-y-1.5 text-sm">
                {rec.reasons.map((r) => <li key={r} className="flex gap-2"><Check className="w-4 h-4 text-secondary shrink-0 mt-0.5" />{r}</li>)}
              </ul>
            </div>
            <div className="mt-4 flex gap-2">
              <Button variant="outline" className="rounded-full" onClick={reset} data-testid="plan-quiz-restart">Start over</Button>
              <Button className="rounded-full flex-1" onClick={() => { onPick(rec.plan); onOpenChange(false); reset(); }} data-testid="plan-quiz-show-plan">
                Show me the {PLAN_LABEL[rec.plan]} plan
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
